import { useTranslation } from 'react-i18next';
import { Badge } from 'reactstrap';
import {
  calculatePickingIndexes,
  MAX_QTY_PER_MISSION_DEFAULT,
  type PickingIndexInput,
  type PickingIndexOptions,
  type HeatLevel,
} from '../../api/pickingIndexes';

// Icona opzionale per fascia. Emoji per leggibilità immediata; il colore lo dà
// la classe .vp-heat-* (vp-custom.scss), coerente col tema verde t/picking.
const HEAT_EMOJI: Record<HeatLevel, string> = {
  cold: '🧊',
  fresh: '🌿',
  normal: '✅',
  warm: '☀️',
  hot: '🔥',
};

export interface PickListHeatBadgeProps {
  input: PickingIndexInput;
  options?: PickingIndexOptions;
}

/**
 * Badge "calore missione" (ICM) per la tabella pick-list. Mostra emoji + valore
 * ICM + label breve; su schermi piccoli la label sparisce e resta solo il pill
 * compatto. Il tooltip (title nativo) riporta il dettaglio IRG/IBR/ICM e i dati
 * di base, più il suggerimento "da frammentare" quando serve.
 */
export function PickListHeatBadge({ input, options }: PickListHeatBadgeProps) {
  const { t } = useTranslation('picklist');
  const ix = calculatePickingIndexes(input, options);
  const maxQty = options?.maxQtyPerMission ?? MAX_QTY_PER_MISSION_DEFAULT;

  const fragDisplay = ix.fragmentation >= 10
    ? Math.round(ix.fragmentation).toString()
    : ix.fragmentation.toFixed(1);

  const lines = [
    t('heat.tooltip.irg', { value: ix.irg }),
    t('heat.tooltip.ibr', { value: ix.ibr }),
    t('heat.tooltip.icm', { value: ix.icm }),
    t('heat.tooltip.locations', { value: input.locations }),
    t('heat.tooltip.qty', { value: input.totalQtyAssigned }),
    t('heat.tooltip.fragmentation', { value: fragDisplay }),
    t('heat.tooltip.max_qty', { value: maxQty }),
  ];
  if (ix.shouldSplitMission) lines.push(t('heat.tooltip.split_hint'));

  return (
    <span className="d-inline-flex align-items-center">
      {/* Warning "da frammentare" FUORI dal pill, sullo sfondo chiaro della cella. */}
      {ix.shouldSplitMission && (
        <i
          className="bi bi-exclamation-triangle-fill text-warning me-1"
          title={t('heat.tooltip.split_hint')}
          aria-label={t('heat.tooltip.split_hint')}
        />
      )}
      <Badge
        pill
        className={`vp-heat vp-heat-${ix.heatLevel}`}
        title={lines.join('\n')}
        aria-label={`${t('heat.column')} ${ix.icm} — ${t(`heat.levels.${ix.heatLevel}`)}`}
      >
        <span aria-hidden="true">{HEAT_EMOJI[ix.heatLevel]}</span>
        <span className="fw-semibold ms-1">{ix.icm}</span>
        <span className="d-none d-md-inline ms-1">{t(`heat.levels.${ix.heatLevel}`)}</span>
      </Badge>
    </span>
  );
}
